<template>
    <div class="activity-page">
        <div class="page-header">
            <div>
                <h1>Activity Log</h1>
                <p>All checklist submissions — who filled which jetty, at what time</p>
            </div>

            <div class="entries-badge" @click="applyFilters">
                <i class="pi pi-refresh"></i>
                <span><strong>{{ filters.pageSize > total ? total : filters.pageSize }}</strong> of {{ total }}
                    entries</span>
            </div>
        </div>

        <div class="filter-card">
            <div class="filter-grid top-row">
                <div class="search-box">
                    <i class="pi pi-search"></i>
                    <input v-model="filters.loadingMasterName" type="text" placeholder="Search name"
                        @keyup.enter="applyFilters" />
                </div>

                <select v-model="filters.jettyNo" class="filter-select">
                    <option value="">All Jetties</option>
                    <option v-for="jetty in jettyOptions" :key="jetty" :value="jetty">
                        Jetty {{ jetty }}
                    </option>
                </select>

                <select v-model="filters.shiftType" class="filter-select">
                    <option value="">All Shifts</option>
                    <option value="DAY">Day</option>
                    <option value="NIGHT">Night</option>
                </select>

                <button class="clear-btn" @click="clearFilters">
                    <i class="pi pi-refresh"></i>
                    <span>Clear Filters</span>
                </button>
            </div>

            <div class="filter-grid bottom-row">
                <div class="date-field">
                    <label>From Date</label>
                    <el-date-picker v-model="filters.startDate" type="date" placeholder="Start date" format="dd/MM/yyyy"
                        value-format="yyyy-MM-dd" class="date-picker-full" clearable />
                </div>

                <div class="date-field">
                    <label>To Date</label>
                    <el-date-picker v-model="filters.endDate" placeholder="End date" type="date" format="dd/MM/yyyy"
                        value-format="yyyy-MM-dd" class="date-picker-full" clearable />
                </div>
            </div>

            <div class="filter-actions">
                <button class="apply-btn" @click="applyFilters">Apply Filters</button>
            </div>
        </div>

        <div class="table-card">
            <div class="table-header-row">
                <div>DATE</div>
                <div>JETTY</div>
                <div>SHIFT</div>
                <div>LOADING MASTER/LEADER NAME</div>
                <div>SUBMITTED AT</div>
                <div>ACTION</div>
            </div>

            <div v-if="loading" class="table-empty">
                Loading activity logs...
            </div>

            <div v-else-if="activityList.length === 0" class="table-empty">
                No activity logs found
            </div>

            <div v-else v-for="item in activityList" :key="item.id" class="table-row">
                <div class="cell date-cell">
                    <div class="main-date">{{ formatDate(item.checkDate) }}</div>
                    <div class="sub-date" v-if="isToday(item.checkDate)">Today</div>
                </div>

                <div class="cell jetty-cell" :style="{ opacity: item.action == 'SIGN' ? '0' : '100' }">
                    <div class="jetty-badge">{{ item.jettyNo }}</div>
                    <span class="jetty-label">J{{ item.jettyNo }}</span>
                </div>

                <div class="cell">
                    <span class="shift-pill" :class="item.shiftType === 'NIGHT' ? 'night' : 'day'">
                        <i :class="item.shiftType === 'NIGHT' ? 'pi pi-moon' : 'pi pi-sun'"></i>
                        {{ formatShift(item.shiftType) }}
                    </span>
                </div>

                <div class="cell master-cell">
                    <div class="avatar-circle">
                        {{ getInitials(item.loadingMasterName) }}
                    </div>
                    <span class="master-name">{{ item.loadingMasterName }}</span>
                </div>

                <div class="cell submitted-cell">
                    <i class="pi pi-clock"></i>
                    <span>{{ formatTime(item.createTime) }}</span>
                </div>

                <div class="cell">
                    <span class="action-pill"
                        :class="item.action === 'UPDATE' ? 'updated' : item.action === 'SIGN' ? 'sign' : item.action === 'DELETE' ? 'delete' : 'created'">
                        <i class="pi pi-refresh" v-if="item.action === 'UPDATE'"></i>
                        <i class="pi pi-pencil" v-if="item.action === 'SIGN'"></i>
                        <i class="pi pi-check-circle" v-if="item.action === 'CREATE'"></i>
                        <i class="pi pi-trash" v-if="item.action === 'DELETE'"></i>
                        {{ formatAction(item.action) }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getJettyActivityLog } from '@/services/user';
import 'primeicons/primeicons.css'

export default {
    name: 'ActivityTab',
    data() {
        return {
            loading: false,
            total: 0,
            activityList: [],
            jettyOptions: [1, 2, 3, 5, 6, 7, 8, 9, 10, 11, 12, 13, 15, 16, 17],
            filters: {
                pageNum: 1,
                pageSize: 10,
                startDate: '',
                endDate: '',
                jettyNo: '',
                loadingMasterName: '',
                shiftType: ''
            }
        }
    },
    mounted() {
        this.getData()
    },
    methods: {
        async getData() {
            this.loading = true

            try {
                const params = {
                    pageNum: this.filters.pageNum,
                    pageSize: this.filters.pageSize,
                    startDate: this.filters.startDate,
                    endDate: this.filters.endDate
                }

                if (this.filters.jettyNo !== '') {
                    params.jettyNo = this.filters.jettyNo
                }

                if (this.filters.loadingMasterName !== '') {
                    params.loadingMasterName = this.filters.loadingMasterName
                }

                if (this.filters.shiftType !== '') {
                    params.shiftType = this.filters.shiftType
                }

                const res = await getJettyActivityLog(params)
                const payload = res.data || {}

                this.total = payload.total || 0
                this.activityList = payload.rows || []
            } catch (error) {
                console.error('getJettyActivityLog error:', error)
                this.total = 0
                this.activityList = []
            } finally {
                this.loading = false
                this.emitPaginationState()
            }
        },

        emitPaginationState() {
            this.$emit('activity-page-state', {
                total: this.total,
                pageNum: this.filters.pageNum,
                pageSize: this.filters.pageSize,
                loading: this.loading
            })
        },

        setPage(pageNum) {
            this.filters.pageNum = pageNum
            this.getData()
        },

        setPageSize(pageSize) {
            this.filters.pageSize = pageSize
            this.filters.pageNum = 1
            this.getData()
        },

        applyFilters() {
            this.filters.pageNum = 1
            this.getData()
        },

        clearFilters() {
            this.filters = {
                pageNum: 1,
                pageSize: 10,
                startDate: '',
                endDate: '',
                jettyNo: '',
                loadingMasterName: '',
                shiftType: ''
            }

            this.getData()
        },

        formatDate(dateStr) {
            const date = new Date(dateStr)
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            })
        },

        formatTime(dateTime) {
            return dateTime || ""
        },

        formatShift(shiftType) {
            if (shiftType === 'NIGHT') return 'Night'
            if (shiftType === 'DAY') return 'Day'
            return shiftType
        },

        formatAction(action) {
            if (action === 'UPDATE') return 'Updated'
            if (action === 'CREATE') return 'Created'
            if (action === 'SIGN') return 'Signed'
            if (action === 'DELETE') return 'Deleted'
            return action
        },

        getInitials(name) {
            if (!name) return '--'

            return name
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map(function (part) {
                    return part.charAt(0).toUpperCase()
                })
                .join('')
        },

        isToday(dateStr) {
            const today = new Date()
            const target = new Date(dateStr)

            const todayFormatted = [
                today.getFullYear(),
                String(today.getMonth() + 1).padStart(2, '0'),
                String(today.getDate()).padStart(2, '0')
            ].join('-')

            const targetFormatted = [
                target.getFullYear(),
                String(target.getMonth() + 1).padStart(2, '0'),
                String(target.getDate()).padStart(2, '0')
            ].join('-')

            return todayFormatted === targetFormatted
        }
    }
}
</script>

<style scoped lang="scss">
.activity-page {
    min-height: 100vh;
    background: #f6f8fb;
    padding: 16px;
}

/* HEADER */

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
}

.page-header h1 {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: #161b26;
}

.page-header p {
    margin-top: 4px;
    font-size: 12px;
    color: #61728d;
}

/* ENTRIES BADGE */

.entries-badge {
    height: 40px;
    padding: 0 14px;
    border: 1px solid #d9dee7;
    background: #fff;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #61728d;
}

.entries-badge i {
    font-size: 14px;
}

.entries-badge strong {
    color: #161b26;
}

/* FILTER CARD */

.filter-card {
    background: #fff;
    border: 1px solid #dfe4ec;
    border-radius: 16px;
    padding: 16px;
    margin-bottom: 16px;
}

.filter-grid {
    display: grid;
    gap: 12px;
}

.top-row {
    grid-template-columns: 1.2fr 1fr 1fr 1fr;
    margin-bottom: 12px;
}

.bottom-row {
    grid-template-columns: 1fr 1fr;
}

/* SEARCH */

.search-box {
    height: 38px;
    border: 1px solid #cfd6e0;
    border-radius: 10px;
    display: flex;
    align-items: center;
    padding: 0 10px;
}

.search-box i {
    font-size: 13px;
    color: #8b97aa;
    margin-right: 6px;
}

.search-box input {
    width: 100%;
    border: none;
    outline: none;
    font-size: 12px;
}

/* SELECTS */

.filter-select {
    height: 38px;
    border: 1px solid #cfd6e0;
    border-radius: 10px;
    padding: 0 36px 0 12px;
    font-size: 12px;
    background-color: #fff;
    outline: none;

    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;

    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238b97aa' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 12px;
}

/* DATE */

.date-field {
    display: flex;
    flex-direction: column;
}

.date-field label {
    font-size: 11px;
    margin-bottom: 4px;
    color: #61728d;
}

.date-picker-full {
    width: 100%;
}

::v-deep(.date-picker-full .el-input__wrapper) {
    min-height: 36px;
    box-shadow: 0 0 0 1px #cfd6e0 inset !important;
    background: #fff;
}

::v-deep(.date-picker-full .el-input__inner) {
    font-size: 12px;
    color: #161b26;
    border-radius: 10px;
}

::v-deep(.date-picker-full .el-input__prefix),
::v-deep(.date-picker-full .el-input__suffix) {
    color: #8b97aa;
}

/* BUTTONS */

.clear-btn {
    height: 38px;
    border: 1px solid #cfd6e0;
    border-radius: 10px;
    background: #fff;
    font-size: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.apply-btn {
    height: 34px;
    padding: 0 12px;
    border-radius: 8px;
    border: none;
    background: #1887e0;
    color: #fff;
    font-size: 12px;
    cursor: pointer;
}

.filter-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
}

/* TABLE */

.table-card {
    background: #fff;
    border: 1px solid #dfe4ec;
    border-radius: 16px;
    overflow: hidden;
}

/* TABLE HEADER */

.table-header-row {
    display: grid;
    grid-template-columns: 1.2fr 0.8fr 1fr 1.6fr 1fr 1fr;
    padding: 12px 14px;
    background: #f8fafc;
    border-bottom: 1px solid #e6ebf2;
    font-size: 11px;
    font-weight: 700;
    color: #6d7c93;
}

/* TABLE ROW */

.table-row {
    display: grid;
    grid-template-columns: 1.2fr 0.8fr 1fr 1.6fr 1fr 1fr;
    padding: 10px 14px;
    align-items: center;
    border-bottom: 1px solid #edf1f5;
}

.table-row:last-child {
    border-bottom: none;
}

.cell {
    min-width: 0;
}

/* DATE */

.date-cell .main-date {
    font-size: 13px;
    font-weight: 700;
}

.date-cell .sub-date {
    font-size: 10px;
    color: #1887e0;
}

/* JETTY */

.jetty-cell {
    display: flex;
    align-items: center;
    gap: 6px;
}

.jetty-badge {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: #1490df;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.jetty-label {
    font-size: 12px;
    color: #61728d;
}

/* SHIFT */

.shift-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 28px;
    padding: 0 8px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
}

.shift-pill.day {
    background: #fff6ea;
    border: 1px solid #f2ca82;
    color: #d97706;
}

.shift-pill.night {
    background: #f2f0ff;
    border: 1px solid #c8c2ff;
    color: #4f46e5;
}

/* MASTER */

.master-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.avatar-circle {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #1178c8;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.master-name {
    font-size: 12px;
}

/* TIME */

.submitted-cell {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #61728d;
}

.submitted-cell i {
    font-size: 12px;
}

/* ACTION */

.action-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 28px;
    padding: 0 8px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
}

.action-pill.updated {
    background: #fff7e7;
    border: 1px solid #f2cc69;
    color: #c96b07;
}

.action-pill.created {
    background: #edf9f1;
    border: 1px solid #8fd7a7;
    color: #1d8b48;
}

.action-pill.sign {
    background: #f4edf9;
    border: 1px solid #b28fd7;
    color: #4b1d8b;
}

.action-pill.delete {
    background: #ffe7e7;
    border: 1px solid #f26969;
    color: #c90707;
}

/* EMPTY */

.table-empty {
    padding: 16px;
    text-align: center;
    font-size: 12px;
    color: #6d7c93;
}

/* MOBILE */

@media (max-width: 1200px) {
    .top-row {
        grid-template-columns: 1fr 1fr;
    }

    /* header labels don't apply once rows become cards */
    .table-header-row {
        display: none;
    }

    /* let each row stand as its own card */
    .table-card {
        border: none;
        background: transparent;
        overflow: visible;
    }

    .table-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px 14px;
        padding: 14px;
        margin-bottom: 10px;
        border: 1px solid #e6ebf2;
        border-radius: 12px;
        background: #fff;
    }

    .table-row:last-child {
        margin-bottom: 0;
        border-bottom: 1px solid #e6ebf2;
    }

    /* name gets its own line so long names don't crush the pills;
       date/jetty/shift sit on top, submitted-time + action below it */
    .master-cell {
        flex-basis: 100%;
        order: 1;
    }

    .submitted-cell {
        order: 2;
    }

    .table-row > .cell:last-child {
        order: 2;
    }
}

@media (max-width: 768px) {
    .activity-page {
        padding: 12px;
    }

    .bottom-row,
    .top-row {
        grid-template-columns: 1fr;
    }

    .page-header {
        flex-direction: column;
    }

    .entries-badge {
        align-self: flex-start;
    }
}
</style>